import { createStore, createTypedHooks } from 'easy-peasy';
import { AppStoreModel, IAppStoreModel } from './appStoreModel';
import { ITokensStoreModel, TokenStoreModel } from './tokensStoreModel';
import { IUseAppStore, IUsePriceStore, IUseTokensStore, IUseWalletStore } from './types';
import { IWalletStoreModel, WalletStoreModel } from './walletStoreModel';

interface IGlobalStoreModel extends IWalletStoreModel, IAppStoreModel, ITokensStoreModel {
    // Additional properties or methods can be defined here if needed
}
const GlobalModel: IGlobalStoreModel = {
    ...WalletStoreModel,
    ...AppStoreModel,
    ...TokenStoreModel,
};

export const globalStore = createStore<IGlobalStoreModel>(GlobalModel);

const { useStoreActions, useStoreState, useStoreDispatch, useStore } = createTypedHooks<IGlobalStoreModel>();

export function useWalletStore(): IUseWalletStore {
    const state = useStoreState((state) => state);
    const actions = useStoreActions((actions) => actions);
    return {
        emulatorDB: state.emulatorDB,
        setEmulatorDB: actions.setEmulatorDB,

        cardanoWallets: state.cardanoWallets,
        setCardanoWallets: actions.setCardanoWallets,
        getCardanoWallets: actions.getCardanoWallets,
        isGettingWalletsDone: state.isGettingWalletsDone,

        swDoNotPromtForSigning: state.swDoNotPromtForSigning,
        setSwDoNotPromtForSigning: actions.setSwDoNotPromtForSigning,

        swHideBalance: state.swHideBalance,
        setSwHideBalance: actions.setSwHideBalance,

        swProInterface: state.swProInterface,
        setSwProInterface: actions.setSwProInterface,

        isConnecting: state.isConnecting,
        isConnected: state.isConnected,

        getLucid: actions.getLucid,
        setLucid: actions.setLucid,
        getLucidForUseAsUtils: actions.getLucidForUseAsUtils,
        setLucidForUseAsUtils: actions.setLucidForUseAsUtils,

        protocolParameters: state.protocolParameters,

        info: state.info,

        getPkh: state.getPkh,

        loadWalletPrivileges: actions.loadWalletPrivileges,
        isLoadingAnyData: state.isLoadingAnyData,
        isWalletPrivilegesLoading: state.isWalletPrivilegesLoading,
        isWalletPrivilegesLoaded: state.isWalletPrivilegesLoaded,
        isWalletDataLoading: state.isWalletDataLoading,
        isWalletDataLoaded: state.isWalletDataLoaded,

        uTxOsAtWallet: state.uTxOsAtWallet,
        getUTxOsAtWallet: state.getUTxOsAtWallet,

        isCoreTeam: state.isCoreTeam,
        isProtocolAdmin: state.isProtocolAdmin,
        isFundAdmin: state.isFundAdmin,
        isTokenGovHolder: state.isTokenGovHolder,

        loadWalletData: actions.loadWalletData,
        checkSessionValidity: actions.checkSessionValidity,
        connectWallet: actions.connectWallet,
        disconnectWallet: actions.disconnectWallet,

        delegations: state.delegations,
        swapOffers: state.swapOffers,

        isSwapOffersLoading: state.isSwapOffersLoading,
        isSwapOffersLoaded: state.isSwapOffersLoaded,

        loadSwapOffers: actions.loadSwapOffers,

        isParticipationLoading: state.isParticipationLoading,
        isParticipationLoaded: state.isParticipationLoaded,
        initLoadingParticipation: actions.initLoadingParticipation,
        swLoadParticipation: state.swLoadParticipation,
        finishLoadingParticipation: actions.finishLoadingParticipation,

        getTotalOfUnit: state.getTotalOfUnit,

        tokenGov: state.tokenGov,
        tokensFT: state.tokensFT,

        getTotalNumberOfTokens: state.getTotalNumberOfTokens,
        getTotalStaked: state.getTotalStaked,
        getTotalValuation: state.getTotalValuation,
    };
}

export function useAppStore(): IUseAppStore {
    const state = useStoreState((state) => state);
    const actions = useStoreActions((actions) => actions);
    return {
        swInitApiCompleted: state.swInitApiCompleted,
        setSwInitApiCompleted: actions.setSwInitApiCompleted,
        isNavigating: state.isNavigating,
        setIsNavigating: actions.setIsNavigating,
        navigationTimeoutId: state.navigationTimeoutId,
        setNavigationTimeoutId: actions.setNavigationTimeoutId,
        swExistProtocol: state.swExistProtocol,
        checkIfExistProtocol: actions.checkIfExistProtocol,
        swExistAnyWallet: state.swExistAnyWallet,
        checkIfExistAnyWallet: actions.checkIfExistAnyWallet,
        siteSettings: state.siteSettings,
        setSiteSettings: actions.setSiteSettings,
        protocol: state.protocol,
        setProtocol: actions.setProtocol,
        isProtocolLoading: state.isProtocolLoading,
        isProtocolLoaded: state.isProtocolLoaded,
        loadProtocol: actions.loadProtocol,
        funds: state.funds,
        setFunds: actions.setFunds,
        isFundsLoading: state.isFundsLoading,
        isFundsLoaded: state.isFundsLoaded,
        loadFunds: actions.loadFunds,
        isAppStoreLoading: state.isAppStoreLoading,
        isAppStoreLoaded: state.isAppStoreLoaded,
        isProcessingTx: state.isProcessingTx,
        setIsProcessingTx: actions.setIsProcessingTx,
    };
}

export function useTokensStore(): IUseTokensStore & IUsePriceStore {
    //--------------------------------------
    const state = useStoreState((state) => state);
    const actions = useStoreActions((actions) => actions);
    //--------------------------------------
    return {
        serverTime: state.serverTime,

        isServerTimeLoaded: state.isServerTimeLoaded,

        jobTokensToAdd: state.jobTokensToAdd,
        jobTokensToAddFinished: state.jobTokensToAddFinished,

        createJobTokensToAdd: actions.createJobTokensToAdd,
        createJobTokensToAddFromFund: actions.createJobTokensToAddFromFund,
        createJobTokensToAddFromFunds: actions.createJobTokensToAddFromFunds,

        getFinishedJobTokensToAdd: state.getFinishedJobTokensToAdd,

        isExecuting: state.isExecuting,
        executeJobsTokensToAdd: actions.executeJobsTokensToAdd,

        tokensWithDetails: state.tokensWithDetails,
        tokensWithDetailsAndValidity: state.tokensWithDetailsAndValidity,

        isAddingTokens: state.isAddingTokens,

        getServerTime: actions.getServerTime,

        intervalIdForUpdateLoop: state.intervalIdForUpdateLoop,
        setDoUpdatePricesAutomatically: actions.setDoUpdatePricesAutomatically,

        beginUpdateLoop: actions.beginUpdateLoop,
        stopUpdateLoop: actions.stopUpdateLoop,

        cleanStore: actions.cleanStore,

        addToken: actions.addToken,
        addTokens: actions.addTokens,

        removeToken: actions.removeToken,

        refreshPrices: actions.refreshPrices,

        isTokenPriceValid: state.isTokenPriceValid,
        getTokenValidity: state.getTokenValidity,
        getTokenPriceAndMetadata: state.getTokenPriceAndMetadata,
        getTokensPriceAndMetadata: state.getTokensPriceAndMetadata,
        getTokenDecimals: state.getTokenDecimals,

        showTokenPriceAndValidity: state.showTokenPriceAndValidity,

        showTokenPrice: state.showTokenPrice,
        showTokenValidity: state.showTokenValidity,

        showTokenWithAmount: state.showTokenWithAmount,
        showTokenPriceMultipliedByAmount: state.showTokenPriceMultipliedByAmount,

        adaUsdPrice: state.adaUsdPrice,
        adaUsdPriceLastUpdated: state.adaUsdPriceLastUpdated,
        adaPriceError: state.adaPriceError,
        isFetchingAdaPrice: state.isFetchingAdaPrice,

        fetchAdaUsdPrice: actions.fetchAdaUsdPrice,

        adaUsdPriceHistoric: state.adaUsdPriceHistoric,
        adaPriceHistoricError: state.adaPriceHistoricError,
        isFetchingAdaPriceHistoric: state.isFetchingAdaPriceHistoric,

        fetchAdaUsdPriceHistoric: actions.fetchAdaUsdPriceHistoric,

        displayCurrency: state.displayCurrency,

        setDisplayCurrency: actions.setDisplayCurrency,
        toggleDisplayCurrency: actions.toggleDisplayCurrency,

    };
}
